import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../theme';

const AdminContainer = styled.div`
  min-height: auto;
  background: transparent;
  padding: 0;
`;

const AdminWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const AdminHeader = styled.div`
  background: ${theme.colors.card};
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 1.5rem;
  border: 1px solid ${theme.colors.border};
`;

const AdminTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const AdminSubtitle = styled.p`
  font-size: 1rem;
  color: ${theme.colors.text.secondary};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  background: ${theme.colors.card};
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 30px;
  border: 1px solid ${theme.colors.border};
`;

const StatValue = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${theme.colors.primary};
  margin-bottom: 10px;
`;

const StatLabel = styled.div`
  font-size: 1rem;
  color: ${theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const FilterContainer = styled.div`
  background: ${theme.colors.card};
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 30px;
  margin-bottom: 30px;
  border: 1px solid ${theme.colors.border};
`;

const FilterRow = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  align-items: end;
`;

const FilterGroup = styled.div`
  flex: 1;
  min-width: 200px;
`;

const FilterLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: ${theme.colors.text.secondary};
`;

const FilterInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${theme.colors.inputBorder};
  border-radius: 8px;
  font-size: 1rem;
  background: ${theme.colors.inputBackground};
  color: ${theme.colors.text.primary};
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }
`;

const FilterSelect = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${theme.colors.inputBorder};
  border-radius: 8px;
  font-size: 1rem;
  background: ${theme.colors.inputBackground};
  color: ${theme.colors.text.primary};
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }

  option {
    background: ${theme.colors.backgroundSolid};
    color: ${theme.colors.text.primary};
  }
`;

const FilterButton = styled.button`
  padding: 12px 24px;
  background: ${theme.colors.primaryGradient};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
  
  &:hover {
    background: ${theme.colors.primaryGradientHover};
  }
`;

const FeedbackGrid = styled.div`
  display: grid;
  gap: 20px;
`;

const FeedbackCard = styled.div`
  background: ${theme.colors.card};
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 30px;
  border: 1px solid ${theme.colors.border};
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    background: ${theme.colors.cardHover};
  }
`;

const FeedbackHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 20px;
`;

const FeedbackInfo = styled.div`
  flex: 1;
`;

const FeedbackName = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin-bottom: 8px;
`;

const FeedbackEmail = styled.p`
  color: ${theme.colors.text.secondary};
  margin-bottom: 8px;
`;

const FeedbackMeta = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 12px;
  background: ${theme.colors.cardSubtle};
  border-radius: 20px;
  font-size: 0.85rem;
  color: ${theme.colors.text.secondary};
  border: 1px solid ${theme.colors.borderLight};
`;

const StatusBadge = styled.span`
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch (props.status) {
      case 'pending':
        return 'background: #fff3cd; color: #856404;';
      case 'reviewed':
        return 'background: #cce5ff; color: #004085;';
      case 'resolved':
        return 'background: #d4edda; color: #155724;';
      default:
        return 'background: #f8f9fa; color: #6c757d;';
    }
  }}
`;

const FeedbackMessage = styled.p`
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
  margin-bottom: 20px;
  font-size: 1rem;
`;

const FeedbackActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return 'background: #3498db; color: white; &:hover { background: #2980b9; }';
      case 'success':
        return 'background: #27ae60; color: white; &:hover { background: #229954; }';
      case 'danger':
        return 'background: #e74c3c; color: white; &:hover { background: #c0392b; }';
      default:
        return 'background: #95a5a6; color: white; &:hover { background: #7f8c8d; }';
    }
  }}
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${theme.colors.overlay};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${theme.colors.modalBackground};
  backdrop-filter: blur(12px);
  border-radius: 16px;
  padding: 40px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  border: 1px solid ${theme.colors.border};
`;

const ModalTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin-bottom: 20px;
`;

const ModalBody = styled.div`
  margin-bottom: 30px;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: ${theme.colors.text.secondary};
  font-size: 1.1rem;
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #f5c6cb;
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #c3e6cb;
`;

const FeedbackAdmin = ({ showMessage, onFeedbackUpdate }) => {
  const [feedback, setFeedback] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  // Fetch feedback data
  const fetchFeedback = async () => {
    try {
      setLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/feedback?${params}`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        }
      });

      if (response.ok) {
        const data = await response.json();
        setFeedback(data.data);
        setPagination(prev => ({
          ...prev,
          ...data.pagination
        }));
      } else {
        console.error('Failed to fetch feedback - Status:', response.status);
        const errorText = await response.text();
        console.error('Error response:', errorText);
      }
    } catch (error) {
      console.error('Error fetching feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics
  const fetchStatistics = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/feedback/statistics`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStatistics(data.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  // Update feedback status
  const updateStatus = async (id, status) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/feedback/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        fetchFeedback(); // Refresh data
        if (selectedFeedback && selectedFeedback.id === id) {
          setSelectedFeedback(prev => ({ ...prev, status }));
        }
        setSuccess('Feedback status updated successfully');
        if (onFeedbackUpdate) onFeedbackUpdate(); // Refresh pending count
      } else {
        console.error('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Delete feedback
  const deleteFeedback = async (id) => {
    if (!window.confirm('Are you sure you want to delete this feedback?')) {
      return;
    }

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/feedback/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        }
      });

      if (response.ok) {
        fetchFeedback(); // Refresh data
        setShowModal(false);
        setSuccess('Feedback deleted successfully');
        if (onFeedbackUpdate) onFeedbackUpdate(); // Refresh pending count
      } else {
        console.error('Failed to delete feedback');
      }
    } catch (error) {
      console.error('Error deleting feedback:', error);
    }
  };

  // View feedback details
  const viewFeedback = async (id) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/feedback/${id}`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedFeedback(data.data);
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error fetching feedback details:', error);
    }
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Apply filters
  const applyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchFeedback();
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({ status: '', type: '', search: '' });
    setPagination(prev => ({ ...prev, page: 1 }));
    setTimeout(fetchFeedback, 0);
  };

  // Pagination controls
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  useEffect(() => {
    fetchFeedback();
    fetchStatistics();
  }, [pagination.page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (loading && feedback.length === 0) {
    return (
      <AdminContainer>
        <AdminWrapper>
          <LoadingMessage>Loading feedback...</LoadingMessage>
        </AdminWrapper>
      </AdminContainer>
    );
  }

  return (
    <AdminContainer>
      <AdminWrapper>
        <AdminHeader>
          <AdminTitle>Feedback Management</AdminTitle>
          <AdminSubtitle>Manage and respond to user feedback</AdminSubtitle>
        </AdminHeader>

        {success && <SuccessMessage>{success}</SuccessMessage>}
        {error && <ErrorMessage>{error}</ErrorMessage>}

        {/* Statistics Cards */}
        <StatsGrid>
          <StatCard>
            <StatValue>{statistics.total || 0}</StatValue>
            <StatLabel>Total Feedback</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{statistics.pending || 0}</StatValue>
            <StatLabel>Pending</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{statistics.resolved || 0}</StatValue>
            <StatLabel>Resolved</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{statistics.average_rating || 0}</StatValue>
            <StatLabel>Avg Rating</StatLabel>
          </StatCard>
        </StatsGrid>

        {/* Filters */}
        <FilterContainer>
          <FilterRow>
            <FilterGroup>
              <FilterLabel>Status</FilterLabel>
              <FilterSelect
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
                <option value="resolved">Resolved</option>
              </FilterSelect>
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>Type</FilterLabel>
              <FilterSelect
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
              >
                <option value="">All Types</option>
                <option value="suggestion">Suggestion</option>
                <option value="bug">Bug Report</option>
                <option value="content">Content</option>
                <option value="technical">Technical</option>
                <option value="other">Other</option>
              </FilterSelect>
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>Search</FilterLabel>
              <FilterInput
                type="text"
                placeholder="Search by name or email..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </FilterGroup>
            <FilterGroup>
              <FilterButton onClick={applyFilters}>Apply Filters</FilterButton>
              <FilterButton onClick={clearFilters}>Clear</FilterButton>
            </FilterGroup>
          </FilterRow>
        </FilterContainer>

        {/* Feedback List */}
        <FeedbackGrid>
          {feedback.map((item) => (
            <FeedbackCard key={item.id}>
              <FeedbackHeader>
                <FeedbackInfo>
                  <FeedbackName>{item.name}</FeedbackName>
                  <FeedbackEmail>{item.email}</FeedbackEmail>
                  <FeedbackMeta>
                    <MetaItem>📝 {item.feedback_type}</MetaItem>
                    <MetaItem>⭐ {item.rating}/5</MetaItem>
                    <MetaItem>📅 {new Date(item.created_at).toLocaleDateString()}</MetaItem>
                  </FeedbackMeta>
                </FeedbackInfo>
                <StatusBadge status={item.status}>
                  {item.status}
                </StatusBadge>
              </FeedbackHeader>

              <FeedbackMessage>
                {item.message.length > 200 
                  ? `${item.message.substring(0, 200)}...` 
                  : item.message
                }
              </FeedbackMessage>

              <FeedbackActions>
                <ActionButton 
                  variant="primary" 
                  onClick={() => viewFeedback(item.id)}
                >
                  View Details
                </ActionButton>
                {item.status === 'pending' && (
                  <ActionButton 
                    variant="success" 
                    onClick={() => updateStatus(item.id, 'reviewed')}
                  >
                    Mark Reviewed
                  </ActionButton>
                )}
                {item.status === 'reviewed' && (
                  <ActionButton 
                    variant="success" 
                    onClick={() => updateStatus(item.id, 'resolved')}
                  >
                    Mark Resolved
                  </ActionButton>
                )}
                <ActionButton 
                  variant="danger" 
                  onClick={() => deleteFeedback(item.id)}
                >
                  Delete
                </ActionButton>
              </FeedbackActions>
            </FeedbackCard>
          ))}
        </FeedbackGrid>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <FilterContainer style={{ marginTop: '30px' }}>
            <FilterRow style={{ justifyContent: 'center' }}>
              <ActionButton
                variant="default"
                disabled={pagination.page === 1}
                onClick={() => handlePageChange(pagination.page - 1)}
              >
                Previous
              </ActionButton>
              <span style={{ padding: '0 20px', display: 'flex', alignItems: 'center' }}>
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <ActionButton
                variant="default"
                disabled={pagination.page === pagination.totalPages}
                onClick={() => handlePageChange(pagination.page + 1)}
              >
                Next
              </ActionButton>
            </FilterRow>
          </FilterContainer>
        )}

        {/* Detail Modal */}
        {showModal && selectedFeedback && (
          <Modal onClick={() => setShowModal(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalTitle>Feedback Details</ModalTitle>
              <ModalBody>
                <FeedbackHeader>
                  <FeedbackInfo>
                    <FeedbackName>{selectedFeedback.name}</FeedbackName>
                    <FeedbackEmail>{selectedFeedback.email}</FeedbackEmail>
                    <FeedbackMeta>
                      <MetaItem>📝 {selectedFeedback.feedback_type}</MetaItem>
                      <MetaItem>⭐ {selectedFeedback.rating}/5</MetaItem>
                      <MetaItem>📅 {new Date(selectedFeedback.created_at).toLocaleDateString()}</MetaItem>
                    </FeedbackMeta>
                  </FeedbackInfo>
                  <StatusBadge status={selectedFeedback.status}>
                    {selectedFeedback.status}
                  </StatusBadge>
                </FeedbackHeader>

                <FeedbackMessage style={{ marginBottom: '30px' }}>
                  {selectedFeedback.message}
                </FeedbackMessage>

                {selectedFeedback.screenshot_url && (
                  <div style={{ marginBottom: '20px' }}>
                    <strong>Screenshot:</strong>
                    <br />
                    <img 
                      src={selectedFeedback.screenshot_url} 
                      alt="Screenshot" 
                      style={{ maxWidth: '100%', borderRadius: '8px', marginTop: '10px' }}
                    />
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                <ActionButton variant="default" onClick={() => setShowModal(false)}>
                  Close
                </ActionButton>
                {selectedFeedback.status === 'pending' && (
                  <ActionButton 
                    variant="success" 
                    onClick={() => {
                      updateStatus(selectedFeedback.id, 'reviewed');
                    }}
                  >
                    Mark Reviewed
                  </ActionButton>
                )}
                {selectedFeedback.status === 'reviewed' && (
                  <ActionButton 
                    variant="success" 
                    onClick={() => {
                      updateStatus(selectedFeedback.id, 'resolved');
                    }}
                  >
                    Mark Resolved
                  </ActionButton>
                )}
                <ActionButton 
                  variant="danger" 
                  onClick={() => {
                    deleteFeedback(selectedFeedback.id);
                  }}
                >
                  Delete
                </ActionButton>
              </ModalFooter>
            </ModalContent>
          </Modal>
        )}
      </AdminWrapper>
    </AdminContainer>
  );
};

export default FeedbackAdmin;
